import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, Volume2, VolumeX, X } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const HOUSE_LOOPS = [
  { id: "house-eq", title: "The Lab Equalizer", description: "House loop — the sound of the lab", video_url: "/api/files/beatsbyatl/media/eq_loop.mp4", poster_url: "/api/files/beatsbyatl/media/eq_loop_poster.webp" },
  { id: "house-studio", title: "Inside the Lab", description: "House loop — slow zoom through the studio", video_url: "/api/files/beatsbyatl/media/studio_zoom.mp4", poster_url: "/api/files/beatsbyatl/media/studio_zoom_poster.webp" },
];

const LAB_PHOTOS = [
  { id: "lab-neon", title: "Neon hours", url: "/api/files/beatsbyatl/media/lab_1.webp" },
  { id: "lab-session", title: "Session in progress", url: "/api/files/beatsbyatl/media/lab_2.webp" },
];

function useFadeAudio() {
  const videoRef = useRef(null);
  const fadeTimerRef = useRef(null);
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = Math.random().toString(36).slice(2, 10);
  const [audible, setAudible] = useState(false);

  const fadeTo = (target, duration = 450) => {
    const vid = videoRef.current;
    if (!vid) return;
    if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
    if (target > 0) {
      try { vid.muted = false; } catch { /* autoplay policy */ }
    }
    const steps = 18;
    const start = vid.volume ?? 0;
    const delta = target - start;
    let i = 0;
    fadeTimerRef.current = setInterval(() => {
      i += 1;
      const v = start + (delta * i) / steps;
      try { vid.volume = Math.max(0, Math.min(1, v)); } catch { /* ignore */ }
      if (i >= steps) {
        clearInterval(fadeTimerRef.current);
        fadeTimerRef.current = null;
        if (target === 0) {
          try { vid.muted = true; } catch { /* ignore */ }
        }
      }
    }, duration / steps);
  };

  const unmute = () => {
    setAudible(true);
    fadeTo(1);
    // Tell every other preview on the page to mute — hard-cap of one audible card.
    try {
      window.dispatchEvent(
        new CustomEvent("prodrushi:preview-audio-claim", { detail: { owner: idRef.current } })
      );
    } catch { /* older browsers */ }
  };
  const mute = () => { setAudible(false); fadeTo(0); };

  useEffect(() => {
    const onClaim = (ev) => {
      if (ev.detail?.owner !== idRef.current) {
        setAudible((wasAudible) => {
          if (wasAudible) fadeTo(0, 180);
          return false;
        });
      }
    };
    window.addEventListener("prodrushi:preview-audio-claim", onClaim);
    return () => window.removeEventListener("prodrushi:preview-audio-claim", onClaim);
  }, []);

  // Scroll-away mute: if the <video> is less than 25% on-screen, snap it quiet.
  // Visitors never hear a card they can't see. Re-entering view does NOT auto-unmute;
  // hover remains the only way to reclaim audio.
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.intersectionRatio < 0.25) {
            setAudible((wasAudible) => {
              if (wasAudible) fadeTo(0, 180);
              return false;
            });
          }
        }
      },
      { threshold: [0, 0.25, 0.5] }
    );
    io.observe(vid);
    return () => io.disconnect();
  }, []);

  useEffect(() => () => {
    if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
    const vid = videoRef.current;
    if (vid) {
      try { vid.volume = 0; vid.muted = true; } catch { /* ignore */ }
    }
  }, []);

  return { videoRef, audible, unmute, mute };
}

function ShowcaseHero({ hero, onOpen }) {
  const { videoRef, audible, unmute, mute } = useFadeAudio();

  return (
    <Reveal>
      <div
        onMouseEnter={unmute}
        onMouseLeave={mute}
        onFocus={unmute}
        onBlur={mute}
        className="group relative mt-10 overflow-hidden rounded-3xl border border-emerald-300/30 transition-colors hover:border-emerald-300/70"
        data-testid="showcase-hero-wrap"
      >
        <button
          type="button"
          onClick={onOpen}
          data-testid="showcase-hero-cover"
          className="block w-full text-left"
          aria-label={`Play ${hero.title} with sound`}
        >
          <div
            className="relative aspect-[16/7] overflow-hidden bg-black bg-cover bg-center"
            style={hero.poster_url ? { backgroundImage: `url(${fileUrl(hero.poster_url)})` } : undefined}
          >
            <video
              ref={videoRef}
              src={fileUrl(hero.video_url)}
              poster={hero.poster_url ? fileUrl(hero.poster_url) : undefined}
              autoPlay muted loop playsInline preload="metadata"
              className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.03]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">Featured recap</p>
              <h2 className="font-heading mt-2 text-2xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
                {hero.title}
              </h2>
              {hero.description && (
                <p className="mt-3 max-w-xl text-sm text-zinc-300 sm:text-base">{hero.description}</p>
              )}
              <span
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-300 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-black transition-transform group-hover:scale-105"
                data-testid="showcase-hero-play"
              >
                <Play size={13} fill="currentColor" /> Play with sound
              </span>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            audible ? mute() : unmute();
          }}
          data-testid="showcase-hero-sound-toggle"
          aria-label={audible ? "Mute hero preview" : "Preview hero with sound"}
          className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/60 text-emerald-200 backdrop-blur-md transition-colors hover:border-emerald-300 hover:text-emerald-100"
        >
          {audible ? <Volume2 size={16} /> : <VolumeX size={16} />}
        </button>
        <span
          className="pointer-events-none absolute top-4 left-4 rounded-full border border-white/10 bg-black/50 px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-zinc-300 backdrop-blur-sm sm:top-6 sm:left-6"
          data-testid="showcase-hero-sound-hint"
        >
          {audible ? "SOUND ON" : "HOVER FOR SOUND"}
        </span>
      </div>
    </Reveal>
  );
}

function GridVideoCard({ video: v, onOpen }) {
  const { videoRef, audible, unmute, mute } = useFadeAudio();
  return (
    <div
      onMouseEnter={unmute}
      onMouseLeave={mute}
      onFocus={unmute}
      onBlur={mute}
      className="group relative overflow-hidden rounded-2xl border border-white/10 transition-colors hover:border-emerald-300/40"
      data-testid={`showcase-video-card-${v.id}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="block w-full text-left"
        aria-label={`Play ${v.title} with sound`}
      >
        <div
          className="relative aspect-video overflow-hidden bg-black bg-cover bg-center"
          style={v.poster_url ? { backgroundImage: `url(${fileUrl(v.poster_url)})` } : undefined}
        >
          <video
            ref={videoRef}
            src={fileUrl(v.video_url)}
            poster={v.poster_url ? fileUrl(v.poster_url) : undefined}
            autoPlay muted loop playsInline preload="metadata"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <span className="absolute inset-0 m-auto flex h-12 w-12 items-center justify-center rounded-full bg-black/60 text-emerald-200 opacity-0 backdrop-blur-md transition-opacity group-hover:opacity-100">
            <Play size={18} fill="currentColor" />
          </span>
        </div>
        <div className="bg-[#141419] p-4">
          <p className="font-heading text-sm font-bold text-white">{v.title}</p>
          {v.description && <p className="mt-1 line-clamp-2 text-xs text-zinc-500">{v.description}</p>}
        </div>
      </button>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); audible ? mute() : unmute(); }}
        data-testid={`showcase-video-sound-${v.id}`}
        aria-label={audible ? "Mute preview" : "Preview with sound"}
        className={`absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-black/60 text-emerald-200 backdrop-blur-md transition-opacity hover:border-emerald-300 hover:text-emerald-100 ${
          audible ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        }`}
      >
        {audible ? <Volume2 size={13} /> : <VolumeX size={13} />}
      </button>
    </div>
  );
}


export default function Showcase() {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
  }, []);

  const hero = videos[0] || null;
  const restVideos = hero ? videos.slice(1) : videos;
  const all = [...restVideos, ...HOUSE_LOOPS];

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8" data-testid="showcase-page">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Visual showcase</p>
        <h1 className="font-heading mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">From the Lab</h1>
        <p className="mt-4 max-w-xl text-sm text-zinc-400 sm:text-base">
          Studio sessions, video shoots and visualizers — straight out of Atlanta. Hover to preview, tap to watch with sound.
        </p>
      </Reveal>

      {hero && <ShowcaseHero hero={hero} onOpen={() => setActive(hero)} />}

      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="showcase-grid">
        {all.map((v, i) => (
          <Reveal key={v.id} delay={(i % 3) * 0.08}>
            <GridVideoCard video={v} onOpen={() => setActive(v)} />
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div className="mt-20">
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Photo gallery</p>
          <h2 className="font-heading mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl">In the Lab</h2>
          <p className="mt-2 max-w-lg text-sm text-zinc-400">
            Still frames from late nights at the studio — where every beat on this site was cooked up.
          </p>
        </div>
      </Reveal>
      <div className="mt-8 grid gap-6 sm:grid-cols-2" data-testid="lab-gallery">
        {LAB_PHOTOS.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <figure
              className="group relative overflow-hidden rounded-2xl border border-white/10"
              data-testid={`lab-photo-${p.id}`}
            >
              <img
                src={fileUrl(p.url)}
                alt={p.title}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <figcaption className="absolute bottom-4 left-5 font-mono text-[11px] uppercase tracking-widest text-emerald-200">
                {p.title}
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
            onClick={() => setActive(null)}
            data-testid="showcase-lightbox"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 260 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-4xl overflow-hidden rounded-3xl border border-white/10 bg-[#0B0B0E]"
            >
              <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
                <p className="font-heading text-sm font-bold text-white">{active.title}</p>
                <button onClick={() => setActive(null)} data-testid="showcase-lightbox-close" className="text-zinc-500 hover:text-white">
                  <X size={18} />
                </button>
              </div>
              <video src={fileUrl(active.video_url)} autoPlay controls loop playsInline className="aspect-video w-full bg-black" data-testid="showcase-lightbox-video" />
              {active.description && <p className="px-5 py-4 text-sm text-zinc-400">{active.description}</p>}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
